import { useState, useCallback, useEffect, useMemo } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/Button'
import { useCloserLeads } from '@/hooks/useCloserLeads'
import { useCloserLeadConvertModal } from '@/hooks/useCloserLeadConvertModal'
import { useCloserMetricsExpanded } from '@/hooks/useCloserMetricsExpanded'
import CloserMetrics from '@/components/CloserLeads/CloserMetrics'
import CloserFunnel from '@/components/CloserLeads/CloserFunnel'
import CloserToolbar from '@/components/CloserLeads/CloserToolbar'
import CloserLeadsTable from '@/components/CloserLeads/CloserLeadsTable'
import CloserLeadManagerModal from '@/components/CloserLeads/Modals/CloserLeadManagerModal'
import ConvertLeadToDealModal from '@/components/CloserLeads/Modals/ConvertLeadToDealModal'
import ConvertElaborateProposalConfirmModal from '@/components/CloserLeads/Modals/ConvertElaborateProposalConfirmModal'
import ConvertProposalElaborationModal from '@/components/CloserLeads/Modals/ConvertProposalElaborationModal'
import { PendingProposalResumeBanner } from '@/components/CloserLeads/PendingProposalResumeBanner'
import {
  clearPendingProposalSession,
  findLeadForPendingSession,
  isLeadPendingProposalElaboration,
  readPendingProposalSession,
  type PendingProposalSession,
} from '@/components/CloserLeads/closerLeadProposalFlow.util'
import type { CloserLead } from '@/types'

const PAGE_SIZE = 25

export default function CloserLeads() {
  const {
    leads,
    visibleLeads, loading, isRefreshing, summary, filters, closers, isAdmin, isAdminOrManager, user,
    fetchLeads, handleDiscardLead, handleDeleteLead,
    period, setPeriod,
  } = useCloserLeads()

  const { expanded: metricsExpanded, toggleExpanded: toggleMetricsExpanded } = useCloserMetricsExpanded()

  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view' | 'discard'>('create')
  const [selectedLead, setSelectedLead] = useState<CloserLead | null>(null)

  const pageCount = Math.max(1, Math.ceil(visibleLeads.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount)
  const pagedLeads = visibleLeads.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  useEffect(() => {
    setPage(1)
  }, [
    filters.search,
    filters.filterStatus,
    filters.filterTemp,
    filters.activeView,
    filters.closerFilter,
    filters.sortField,
  ])

  const funnelStages = [
    { label: 'Leads Recebidos', count: summary.total, color: '#3b82f6' },
    { label: 'Em Contato', count: summary.emContato, color: '#f59e0b' },
    { label: 'Convertidos', count: summary.convertidos, color: '#22c55e' },
  ]

  const openCreate = useCallback(() => {
    setSelectedLead(null)
    setModalMode('create')
    setModalOpen(true)
  }, [])

  const openDiscard = useCallback((lead: CloserLead) => {
    setSelectedLead(lead)
    setModalMode('discard')
    setModalOpen(true)
  }, [])

  const openDetails = useCallback((lead: CloserLead) => {
    setSelectedLead(lead)
    setModalMode('view')
    setModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
    setSelectedLead(null)
  }, [])

  const handleSuccess = useCallback(() => {
    fetchLeads()
  }, [fetchLeads])

  const convertModal = useCloserLeadConvertModal(handleSuccess)
  const [pendingSession, setPendingSession] = useState<PendingProposalSession | null>(() =>
    readPendingProposalSession(),
  )

  useEffect(() => {
    if (loading) return
    const session = readPendingProposalSession()
    if (!session) {
      setPendingSession(null)
      return
    }
    const lead = findLeadForPendingSession(leads, session)
    if (lead && !isLeadPendingProposalElaboration(lead)) {
      clearPendingProposalSession()
      setPendingSession(null)
      return
    }
    setPendingSession(session)
  }, [loading, leads])

  const showResumeBanner = useMemo(() => {
    if (!pendingSession || convertModal.proposalEditorDealId) return false
    return true
  }, [pendingSession, convertModal.proposalEditorDealId])

  const handleDismissPendingProposal = useCallback(() => {
    convertModal.dismissPendingProposal()
    setPendingSession(null)
  }, [convertModal])

  const handleResumePendingProposal = useCallback(() => {
    if (!pendingSession) return
    const lead = findLeadForPendingSession(leads, pendingSession)
    if (lead) {
      convertModal.resumeProposalForLead(lead)
      return
    }
    convertModal.openProposalEditor(pendingSession.dealId, {
      leadId: pendingSession.leadId,
      empresa: pendingSession.empresa,
    })
  }, [convertModal, leads, pendingSession])

  return (
    <DashboardLayout title='Minha Carteira'>
      <div className='space-y-5'>
        <CloserToolbar
          filters={filters}
          closers={closers}
          showCloserFilter={isAdmin}
          onNewLead={openCreate}
          metricsExpanded={metricsExpanded}
          onToggleMetrics={toggleMetricsExpanded}
        />

        <CloserMetrics
          summary={summary}
          loading={loading}
          period={period}
          onPeriodChange={setPeriod}
          isAdminOrManager={isAdminOrManager}
          funnel={<CloserFunnel stages={funnelStages} />}
          expanded={metricsExpanded}
        />

        {showResumeBanner && pendingSession ? (
          <PendingProposalResumeBanner
            session={pendingSession}
            onResume={handleResumePendingProposal}
            onDismiss={handleDismissPendingProposal}
          />
        ) : null}

        <CloserLeadsTable
          leads={pagedLeads}
          loading={loading}
          isRefreshing={isRefreshing}
          filters={filters}
          onOpenLead={openDetails}
          onConvertToDeal={convertModal.requestConvert}
          onStartNewNegotiation={convertModal.requestNewNegotiation}
          onResumeProposal={convertModal.resumeProposalForLead}
          onOpenDiscard={openDiscard}
          onDiscardLocked={(lead) => {
            const motivo = lead.exclusividade
              ? `Já em negociação com ${lead.exclusividade.comercial}`
              : 'Já em negociação com outro comercial'
            if (!window.confirm(`Descartar ${lead.empresa}? ${motivo}.`)) return
            void handleDiscardLead(lead.id, 'OUTRO_COMERCIAL', motivo)
          }}
          onDeleteLead={handleDeleteLead}
          isAdminOrManager={isAdminOrManager}
          currentUserId={user?.id}
        />

        {pageCount > 1 && (
          <div className='flex items-center justify-between text-sm text-[var(--font-color-muted)]'>
            <span>
              {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, visibleLeads.length)} de {visibleLeads.length}
            </span>
            <div className='flex gap-2'>
              <Button type='button' variant='ghost' size='sm' disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                Anterior
              </Button>
              <Button type='button' variant='ghost' size='sm' disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)}>
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>

      <CloserLeadManagerModal
        open={modalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
        lead={selectedLead}
        onSuccess={handleSuccess}
        onDiscardLead={handleDiscardLead}
        onRequestConvertToDeal={(lead) => {
          handleCloseModal()
          convertModal.requestConvert(lead, {
            forceNew: lead.status === 'CONVERTIDO',
          })
        }}
      />

      <ConvertLeadToDealModal
        open={convertModal.open}
        target={convertModal.target}
        dataHora={convertModal.dataHora}
        tipo={convertModal.tipo}
        submitting={convertModal.submitting}
        onClose={convertModal.close}
        onDataHoraChange={convertModal.setDataHora}
        onTipoChange={convertModal.setTipo}
        onSubmit={convertModal.handleSubmitClick}
      />

      <ConvertElaborateProposalConfirmModal
        open={convertModal.elaborateConfirmOpen}
        submitting={convertModal.submitting}
        onClose={() => convertModal.setElaborateConfirmOpen(false)}
        onConfirm={convertModal.confirmElaborateNow}
        onDecline={convertModal.confirmElaborateLater}
      />

      <ConvertProposalElaborationModal
        open={Boolean(convertModal.proposalEditorDealId)}
        dealId={convertModal.proposalEditorDealId}
        onClose={convertModal.closeProposalEditor}
      />
    </DashboardLayout>
  )
}
